import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DrawerModule } from 'primeng/drawer';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';

import { Equipamento } from '@core/models/models';
import { FeedbackService } from '@core/services/feedback.service';
import { EquipamentoService } from '@core/services/domain-services';
import { environment } from '@environments/environment';

export interface ApoliceSeguro {
  id?: string | number;
  equipamentoId: number;
  seguradora: string;
  numeroApolice: string;
  tipoSeguro: string;
  dataInicio: string;
  dataFim: string;
  premioAnual: number;
  capitalSeguro?: number;
  franquia?: number;
  contactoSeguradora?: string;
  observacoes?: string;
  ativo: boolean;
  documentoApoliceNome?: string;
  comprovativoPagamentoNome?: string;
  criadoEm?: string;
}

@Component({
  selector: 'app-equipamento-seguro-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DatePickerModule,
    DrawerModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    TooltipModule,
  ],
  templateUrl: './equipamento-seguro-tab.component.html',
  styleUrl: './equipamento-seguro-tab.component.scss',
})
export class EquipamentoSeguroTabComponent implements OnInit, OnChanges {
  private readonly feedback = inject(FeedbackService);
  private readonly equipSvc = inject(EquipamentoService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  @Input() equipamento: Equipamento | null = null;
  @Input() viaturasDisponiveis: Equipamento[] = [];
  @Input() viaturaBloqueada = true;

  @Output() apoliceChange = new EventEmitter<ApoliceSeguro>();

  drawerVisible = false;
  saving = false;

  readonly seguradorasPredefinidas = [
    { label: 'ENSA Seguros', value: 'ENSA' },
    { label: 'Global Seguros', value: 'Global Seguros' },
    { label: 'Nossa Seguros', value: 'Nossa Seguros' },
    { label: 'Fidelidade Angola', value: 'Fidelidade Angola' },
    { label: 'Sanlam Angola', value: 'Sanlam Angola' },
    { label: 'Saham Seguros', value: 'Saham Seguros' },
    { label: 'BIC Seguros', value: 'BIC Seguros' },
    { label: 'Fortaleza Seguros', value: 'Fortaleza Seguros' },
    { label: 'Outra Seguradora', value: 'Outra' },
  ];

  readonly tiposCobertura = [
    { label: 'Responsabilidade Civil', value: 'Responsabilidade Civil' },
    { label: 'RC + Danos Próprios', value: 'RC + Danos Próprios' },
    { label: 'Cobertura Completa', value: 'Cobertura Completa' },
  ];

  apoliceAtiva: ApoliceSeguro | null = null;
  apolicesAnteriores: ApoliceSeguro[] = [];

  form = {
    equipamentoId: null as number | null,
    seguradora: 'ENSA',
    seguradoraCustom: '',
    numeroApolice: '',
    tipoCobertura: 'RC + Danos Próprios',
    dataInicio: new Date() as Date | null,
    dataFim: new Date(new Date().setFullYear(new Date().getFullYear() + 1)) as Date | null,
    premioAnual: null as number | null,
    capitalSegurado: null as number | null,
    franquia: null as number | null,
    contactoSeguradora: '+244 222 345 678',
    observacoes: '',
  };

  apoliceFile: File | null = null;
  comprovativoFile: File | null = null;

  ngOnInit(): void {
    this.carregarApolices();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['equipamento']) {
      this.carregarApolices();
    }
  }

  private getStorageKey(): string {
    const id = this.equipamento?.id ?? this.form.equipamentoId ?? 0;
    return `sp_apolices_equip_${id}`;
  }

  get viaturaSelecionadaInfo(): string {
    const eq = this.equipamento || this.viaturasDisponiveis.find(v => v.id === this.form.equipamentoId);
    if (!eq) return 'Nenhuma viatura selecionada';
    const matricula = this.getPropriedadeValorFrom(eq, 'MATRICULA');
    const marca = this.getPropriedadeValorFrom(eq, 'MARCA');
    const modelo = this.getPropriedadeValorFrom(eq, 'MODELO');
    const parts = [matricula, marca, modelo].filter(Boolean);
    return parts.length ? parts.join(' — ') : (eq.codigoInterno || `Viatura #${eq.id}`);
  }

  get estadoPrevistoCalculado(): { label: string; class: string } {
    if (!this.form.dataFim) return { label: 'Data de fim não definida', class: 'text-muted' };
    const fim = new Date(this.form.dataFim);
    fim.setHours(23, 59, 59, 999);
    const hoje = new Date();
    return fim >= hoje
      ? { label: 'Ativo (Válido)', class: 'text-success' }
      : { label: 'Expirado', class: 'text-danger' };
  }

  carregarApolices(): void {
    if (!this.equipamento) return;

    const stored = localStorage.getItem(this.getStorageKey());
    let list: ApoliceSeguro[] = [];

    if (stored) {
      try {
        list = JSON.parse(stored);
      } catch {
        list = [];
      }
    }

    if (!list.length) {
      const validadeProp = this.getPropriedadeValor('VALIDADE_SEGURO');
      const seguradoraProp = this.getPropriedadeValor('SEGURADORA') || 'ENSA';
      const apoliceProp =
        this.getPropriedadeValor('NUMERO_APOLICE') ||
        this.getPropriedadeValor('APOLICE') ||
        `APO-${new Date().getFullYear()}-00${this.equipamento.id ?? 456}`;
      const tipoProp =
        this.getPropriedadeValor('TIPO_SEGURO') || 'RC + Danos Próprios';
      const premioProp = Number(this.getPropriedadeValor('PREMIO_ANUAL')) || 175000;
      const capitalProp = Number(this.getPropriedadeValor('CAPITAL_SEGURO')) || 15000000;
      const franquiaProp = Number(this.getPropriedadeValor('FRANQUIA')) || 250000;
      const contactoProp =
        this.getPropriedadeValor('CONTACTO_SEGURADORA') || '+244 222 345 678';

      const dataFim = validadeProp
        ? validadeProp.slice(0, 10)
        : `${new Date().getFullYear()}-12-31`;

      const dataInicio = `${new Date(dataFim).getFullYear()}-01-01`;

      const apoliceBase: ApoliceSeguro = {
        equipamentoId: this.equipamento.id!,
        seguradora: seguradoraProp,
        numeroApolice: apoliceProp,
        tipoSeguro: tipoProp,
        dataInicio,
        dataFim,
        premioAnual: premioProp,
        capitalSeguro: capitalProp,
        franquia: franquiaProp,
        contactoSeguradora: contactoProp,
        ativo: new Date(dataFim) >= new Date(),
      };

      const apoliceAntiga1: ApoliceSeguro = {
        equipamentoId: this.equipamento.id!,
        seguradora: seguradoraProp,
        numeroApolice: `APO-${new Date().getFullYear() - 1}-00312`,
        tipoSeguro: 'RC + Danos Próprios',
        dataInicio: `${new Date().getFullYear() - 1}-01-01`,
        dataFim: `${new Date().getFullYear() - 1}-12-31`,
        premioAnual: 160000,
        capitalSeguro: 12000000,
        franquia: 200000,
        contactoSeguradora: contactoProp,
        ativo: false,
      };

      const apoliceAntiga2: ApoliceSeguro = {
        equipamentoId: this.equipamento.id!,
        seguradora: 'Global Seguros',
        numeroApolice: `APO-${new Date().getFullYear() - 2}-00187`,
        tipoSeguro: 'Responsabilidade Civil',
        dataInicio: `${new Date().getFullYear() - 2}-03-10`,
        dataFim: `${new Date().getFullYear() - 1}-03-09`,
        premioAnual: 120000,
        capitalSeguro: 10000000,
        franquia: 150000,
        contactoSeguradora: '+244 923 111 222',
        ativo: false,
      };

      list = [apoliceBase, apoliceAntiga1, apoliceAntiga2];
      localStorage.setItem(this.getStorageKey(), JSON.stringify(list));
    }

    this.apoliceAtiva = list.find((a) => a.ativo) ?? list[0] ?? null;
    this.apolicesAnteriores = list.filter((a) => a !== this.apoliceAtiva);
  }

  openRenovar(): void {
    const targetEquipId = this.equipamento?.id ?? (this.viaturasDisponiveis[0]?.id ?? null);
    const hoje = new Date();
    const anoSeguinte = new Date();
    anoSeguinte.setFullYear(hoje.getFullYear() + 1);

    if (this.apoliceAtiva) {
      this.form = {
        equipamentoId: targetEquipId,
        seguradora: this.apoliceAtiva.seguradora,
        seguradoraCustom: '',
        numeroApolice: this.gerarNumeroApolice(this.apoliceAtiva.seguradora),
        tipoCobertura: this.apoliceAtiva.tipoSeguro || 'RC + Danos Próprios',
        dataInicio: hoje,
        dataFim: anoSeguinte,
        premioAnual: this.apoliceAtiva.premioAnual,
        capitalSegurado: this.apoliceAtiva.capitalSeguro ?? null,
        franquia: this.apoliceAtiva.franquia ?? null,
        contactoSeguradora: this.apoliceAtiva.contactoSeguradora || '+244 222 345 678',
        observacoes: '',
      };
    } else {
      this.form = {
        equipamentoId: targetEquipId,
        seguradora: 'ENSA',
        seguradoraCustom: '',
        numeroApolice: `APO-${new Date().getFullYear()}-00001`,
        tipoCobertura: 'RC + Danos Próprios',
        dataInicio: hoje,
        dataFim: anoSeguinte,
        premioAnual: 175000,
        capitalSegurado: 15000000,
        franquia: 250000,
        contactoSeguradora: '+244 222 345 678',
        observacoes: '',
      };
    }

    this.apoliceFile = null;
    this.comprovativoFile = null;
    this.drawerVisible = true;
  }

  closeDrawer(): void {
    if (this.saving) return;
    this.drawerVisible = false;
    this.apoliceFile = null;
    this.comprovativoFile = null;
  }

  onApoliceFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.apoliceFile = input.files[0];
    }
  }

  removeApoliceFile(): void {
    this.apoliceFile = null;
  }

  onComprovativoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.comprovativoFile = input.files[0];
    }
  }

  removeComprovativoFile(): void {
    this.comprovativoFile = null;
  }

  salvarApolice(): void {
    const targetEquipId = this.equipamento?.id ?? this.form.equipamentoId;
    if (!targetEquipId) {
      this.feedback.showWarning('Selecione uma viatura para associar o seguro.');
      return;
    }

    if (!this.form.numeroApolice || !this.form.numeroApolice.trim()) {
      this.feedback.showWarning('Informe o número da apólice.');
      return;
    }

    if (!this.form.dataInicio || !this.form.dataFim) {
      this.feedback.showWarning('Informe as datas de início e fim de vigência.');
      return;
    }

    this.saving = true;

    const seguradoraFinal =
      this.form.seguradora === 'Outra' && this.form.seguradoraCustom
        ? this.form.seguradoraCustom.trim()
        : this.form.seguradora;

    const dataInicioStr = this.formatDateISO(this.form.dataInicio);
    const dataFimStr = this.formatDateISO(this.form.dataFim);

    // Estado calculado automaticamente: Activo se dataFim >= hoje
    const fim = new Date(this.form.dataFim);
    fim.setHours(23, 59, 59, 999);
    const isAtivo = fim >= new Date();

    const novaApolice: ApoliceSeguro = {
      id: Date.now(),
      equipamentoId: targetEquipId,
      seguradora: seguradoraFinal,
      numeroApolice: this.form.numeroApolice.trim(),
      tipoSeguro: this.form.tipoCobertura,
      dataInicio: dataInicioStr,
      dataFim: dataFimStr,
      premioAnual: this.form.premioAnual ?? 0,
      capitalSeguro: this.form.capitalSegurado ?? undefined,
      franquia: this.form.franquia ?? undefined,
      contactoSeguradora: this.form.contactoSeguradora.trim(),
      observacoes: this.form.observacoes.trim(),
      ativo: isAtivo,
      documentoApoliceNome: this.apoliceFile?.name,
      comprovativoPagamentoNome: this.comprovativoFile?.name,
      criadoEm: new Date().toISOString(),
    };

    // Histórico: as apólices anteriores ficam com ativo: false
    const stored = localStorage.getItem(`sp_apolices_equip_${targetEquipId}`);
    let list: ApoliceSeguro[] = [];
    if (stored) {
      try {
        list = JSON.parse(stored);
      } catch {
        list = [];
      }
    }

    list = list.map((a) => ({ ...a, ativo: false }));
    list.unshift(novaApolice);

    localStorage.setItem(`sp_apolices_equip_${targetEquipId}`, JSON.stringify(list));

    // Upload dos documentos para refletir na tab Documentação
    const uploads$ = [];

    if (this.apoliceFile) {
      const fd = new FormData();
      fd.append('Designacao', `Apólice de Seguro - ${novaApolice.numeroApolice}`);
      fd.append('Ficheiro', this.apoliceFile, this.apoliceFile.name);
      uploads$.push(
        this.http.put<any>(
          `${environment.apiurl}/api/Equipamentos/${targetEquipId}/AdicionarDocumento`,
          fd,
        ).pipe(catchError((e) => {
          console.warn('Erro ao anexar apólice:', e);
          return of(null);
        }))
      );
    }

    if (this.comprovativoFile) {
      const fd = new FormData();
      fd.append('Designacao', `Comprovativo de Pagamento Seguro - ${novaApolice.numeroApolice}`);
      fd.append('Ficheiro', this.comprovativoFile, this.comprovativoFile.name);
      uploads$.push(
        this.http.put<any>(
          `${environment.apiurl}/api/Equipamentos/${targetEquipId}/AdicionarDocumento`,
          fd,
        ).pipe(catchError((e) => {
          console.warn('Erro ao anexar comprovativo:', e);
          return of(null);
        }))
      );
    }

    const finalize = () => {
      this.saving = false;
      this.drawerVisible = false;
      this.carregarApolices();
      this.apoliceChange.emit(novaApolice);
      this.feedback.showSuccess(
        this.apoliceAtiva
          ? 'Apólice de seguro renovada e documentos anexados com sucesso!'
          : 'Apólice de seguro registada e documentos anexados com sucesso!'
      );
    };

    if (uploads$.length) {
      forkJoin(uploads$).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => finalize(),
        error: () => finalize(),
      });
    } else {
      finalize();
    }
  }

  isVigenciaValida(dataFim?: string): boolean {
    if (!dataFim) return false;
    const fim = new Date(dataFim);
    fim.setHours(23, 59, 59, 999);
    const hoje = new Date();
    return fim >= hoje;
  }

  private gerarNumeroApolice(seguradora: string): string {
    const prefix = (seguradora || 'APO').slice(0, 3).toUpperCase();
    const ano = new Date().getFullYear();
    const random = Math.floor(10000 + Math.random() * 90000);
    return `APO-${prefix}-${ano}-${random}`;
  }

  private formatDateISO(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDate(value?: string | Date | null): string {
    if (!value) return '—';
    try {
      const d = value instanceof Date ? value : new Date(value);
      return d.toLocaleDateString('pt-AO');
    } catch {
      return String(value);
    }
  }

  formatKz(value?: number | null): string {
    if (value === null || value === undefined) return '—';
    return (
      new Intl.NumberFormat('pt-AO', {
        maximumFractionDigits: 0,
      }).format(value) + ' Kz'
    );
  }

  private getPropriedadeValor(nome: string): string {
    if (!this.equipamento) return '';
    return this.getPropriedadeValorFrom(this.equipamento, nome);
  }

  private getPropriedadeValorFrom(eq: Equipamento, nome: string): string {
    return (
      eq?.propriedades?.find(
        (p) => p.nome?.toUpperCase() === nome.toUpperCase()
      )?.valor ?? ''
    );
  }
}
